'use client';

import { usePathname } from 'next/navigation';
import { HIDDEN_SECTIONS, isHiddenHref } from '@/lib/hidden_features';
import Link from 'next/link';
import { useState, useEffect } from 'react';
import WorkspaceSwitcher from './WorkspaceSwitcher';
import { useAgencyBranding } from './AgencyBrandingProvider';
import { launchLeadScraper } from '@/lib/lead_scraper_client';

export default function SidebarNav() {
    const pathname = usePathname() || '';
    const { branding } = useAgencyBranding();
    const [isCollapsed, setIsCollapsed] = useState(false);
    const [isMobile, setIsMobile] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    // Simple Mode (default ON): show 9 core destinations instead of the full
    // 80-link tree. "All features" flips to the complete menu. Persisted.
    const [simpleMode, setSimpleMode] = useState(true);
    useEffect(() => { try { setSimpleMode(localStorage.getItem('sidebar-simple') !== '0'); } catch {} }, []);
    const toggleSimple = () => setSimpleMode((v) => { const n = !v; try { localStorage.setItem('sidebar-simple', n ? '1' : '0'); } catch {} return n; });
    // Owner-only items (e.g. Tutorial Studio) are hidden on white-label client installs.
    const [ownerMode, setOwnerMode] = useState(false);
    useEffect(() => { fetch('/api/tutorial').then((r) => r.json()).then((d) => setOwnerMode(!!d.owner)).catch(() => {}); }, []);
    // Default: only the two most-used sections are open, the rest collapse to
    // tidy headers so the sidebar reads as a short menu instead of a 48-item
    // wall. Click any header to expand it (state persists for the session).
    const [expandedSections, setExpandedSections] = useState({
        overview: true,
        campaigns: true,
        agency: false,
        seo: false,
        websites: false,
        content: false,
        channels: false,
        system: false,
        getmore: false
    });

    // On mobile, the sidebar is an off-canvas drawer. When it IS open, it
    // should always render in full-width mode regardless of the user's
    // desktop collapse preference — labels visible, sections expandable.
    // The actual show/hide is driven by the .sidebar-mobile-open class
    // managed by <MobileHeader />; here we just suppress the collapsed
    // rendering when in the mobile breakpoint.
    const effectiveCollapsed = isMobile ? false : isCollapsed;

    // Check localStorage on mount + watch the mobile breakpoint.
    useEffect(() => {
        const savedCollapsed = localStorage.getItem('sidebar-collapsed') === 'true';
        setIsCollapsed(savedCollapsed);
        if (savedCollapsed) {
            document.documentElement.classList.add('sidebar-collapsed');
        } else {
            document.documentElement.classList.remove('sidebar-collapsed');
        }

        const mql = window.matchMedia('(max-width: 759px)');
        const sync = () => setIsMobile(mql.matches);
        sync();
        // Modern + legacy listener registration for broad browser support.
        if (mql.addEventListener) mql.addEventListener('change', sync);
        else mql.addListener(sync);
        return () => {
            if (mql.removeEventListener) mql.removeEventListener('change', sync);
            else mql.removeListener(sync);
        };
    }, []);

    const toggleCollapse = () => {
        const nextState = !isCollapsed;
        setIsCollapsed(nextState);
        localStorage.setItem('sidebar-collapsed', nextState ? 'true' : 'false');
        if (nextState) {
            document.documentElement.classList.add('sidebar-collapsed');
        } else {
            document.documentElement.classList.remove('sidebar-collapsed');
        }
    };

    const toggleSection = (section) => {
        if (effectiveCollapsed) return;
        setExpandedSections(prev => ({
            ...prev,
            [section]: !prev[section]
        }));
    };

    const isActive = (href) => {
        if (href === '/') {
            return pathname === '/';
        }
        return pathname.startsWith(href);
    };

    // Simple Mode — the doors that cover 90% of daily work. Everything else
    // lives one click away behind "All features".
    const SIMPLE_ITEMS = [
        { href: '/dashboard', label: 'Dashboard', icon: '📊' },
        { href: '/lead-finder', label: 'Find Leads', icon: '🧲' },
        { href: '/meineheimat', label: 'meineheimat24', icon: '🏘️' },
        { href: '/campaigns', label: 'Outreach', icon: '✉️' },
        { href: '/unibox', label: 'Inbox', icon: '📥' },
        { href: '/link-building', label: 'AI Linkbuilding', icon: '🤖' },
        { href: '/indexer', label: 'Instant Indexing', icon: '⚡' },
        { href: 'https://voxychat.co', label: 'Build a Chatbot', icon: '💬', external: true },
        { href: '/connections', label: 'Connections', icon: '🔗' },
        { href: '/agency-hq', label: 'Clients & Money', icon: '🏢' },
        { href: '/settings', label: 'Settings', icon: '⚙️' },
    ];

    // Navigation items with categories
    const menuItems = [
        {
            category: 'overview',
            categoryLabel: 'Overview',
            items: [
                { href: '/connections', label: 'Connections', icon: '🔗', color: '#0ea5e9', badge: 'ACCOUNTS' },
                { href: '/get-started', label: 'Get Started', icon: '🚀', color: '#ea580c', badge: 'START', pulse: true },
                { href: '/dashboard', label: 'Dashboard', icon: '📊', color: '#2563eb' },
                { href: '/alerts', label: 'Alerts', icon: '🔔', color: '#dc2626', badge: 'NEW', pulse: true },
            ]
        },
        {
            category: 'leads',
            categoryLabel: 'Leads & CRM',
            items: [
                { href: '/lead-finder', label: 'Lead Finder', icon: '🧲', color: '#ea580c', badge: 'NEW', pulse: true },
                { href: '/meineheimat', label: 'meineheimat24 Feed', icon: '🏘️', color: '#dc2626', badge: 'NEW' },
                { href: '/leads', label: 'Smart Lead CRM', icon: '🎯', color: '#f97316' },
                { href: '/reactivation', label: 'Lead Reactivation', icon: '💸', color: '#ef4444', badge: 'NEW', pulse: true },
                { href: '/competitor-xray', label: 'Competitor X-Ray', icon: '🛰️', color: '#2563eb', badge: 'NEW', pulse: true },
                { href: '/agency', label: 'Prospecting Hub', icon: '🎯', color: '#2563eb', pulse: true, badge: 'NEW' },
                { href: '/pipeline', label: 'Pipeline & ROI', icon: '📈', color: '#10b981', badge: 'NEW', pulse: true },
                { href: '/workflows', label: 'Automation Workflows', icon: '⚡', color: '#f59e0b', badge: 'NEW', pulse: true },
            ]
        },
        {
            category: 'outreach',
            categoryLabel: 'Outreach & Inbox',
            items: [
                { href: '/campaigns', label: 'Outreach Campaigns', icon: '✉️', color: '#2563eb', pulse: true, badge: 'PRO' },
                { href: '/broadcasts', label: 'Email Marketing', icon: '📣', color: '#2563eb', badge: 'NEW', pulse: true },
                { href: '/scheduled', label: 'Scheduled Outreach', icon: '🗓', color: '#2563eb', badge: 'NEW' },
                { href: '/campaigns/analytics', label: 'Campaign Analytics', icon: '📈', color: '#10b981' },
                { href: '/unibox', label: 'Unified Inbox', icon: '📥', color: '#2563eb' },
                { href: '/inbox-intelligence', label: 'Inbox Zero AI', icon: '🧠', color: '#8b5cf6', badge: 'NEW', pulse: true },
                { href: '/approvals', label: 'Approvals', icon: '⚖️', color: '#f59e0b' },
                { href: '/bookings', label: 'Booking & Calendar', icon: '📅', color: '#2563eb', badge: 'NEW', pulse: true },
            ]
        },
        {
            category: 'video',
            categoryLabel: 'Video Studio',
            items: [
                { href: '/youtube-studio', label: 'YouTube Creator Kit', icon: '🎬', color: '#ef4444', badge: 'WOW', pulse: true },
                { href: '/youtube', label: 'YouTube Optimizer', icon: '▶️', color: '#ef4444', badge: 'NEW', pulse: true },
                { href: '/video-lab', label: 'Viral Video Lab', icon: '🔥', color: '#2563eb', badge: 'NEW', pulse: true },
                { href: '/script-studio', label: 'Script Studio', icon: '🎬', color: '#2563eb', badge: 'WOW', pulse: true },
                { href: '/thumbnails', label: 'Thumbnail Studio', icon: '🖼️', color: '#e11d48', badge: 'WOW', pulse: true },
                { href: '/image-studio', label: 'Image Generator', icon: '🎨', color: '#2563eb', badge: 'NEW', pulse: true },
                { href: '/competitor-seo', label: 'Competitor SEO', icon: '🔍', color: '#059669', badge: 'NEW', pulse: true },
                { href: '/video-merge', label: 'Video Merger', icon: '🔗', color: '#059669', badge: 'NEW', pulse: true },
                { href: '/faceless', label: 'Faceless Autopilot', icon: '🤖', color: '#ef4444', badge: 'NEW', pulse: true },
                { href: '/long-video', label: 'Long Video Studio', icon: '🎬', color: '#16a34a', badge: 'NEW', pulse: true },
                { href: '/animation-studio', label: 'Animation Studio', icon: '🎞️', color: '#7c3aed', badge: 'NEW', pulse: true },
                { href: '/tutorial', label: 'Tutorial Studio', icon: '🎓', color: '#2563eb', badge: 'OWNER', pulse: true, ownerOnly: true },
                { href: '/live', label: 'YouTube Live', icon: '🔴', color: '#dc2626', badge: 'NEW', pulse: true },
                { href: '/voice-studio', label: 'Voice Studio (clone your voice)', icon: '🎙', color: '#2563eb', badge: 'NEW', pulse: true },
                { href: '/audio-studio', label: 'Audio Studio', icon: '🎧', color: '#2563eb', badge: 'WOW', pulse: true },
                { href: '/screen-recorder', label: 'Screen Recorder', icon: '🎥', color: '#f97316', badge: 'NEW', pulse: true },
                { href: '/studio', label: 'Studio (Timeline)', icon: '🎬', color: '#2563eb', badge: 'NEW', pulse: true },
                { href: '/library', label: 'Media Library', icon: '🎞️', color: '#f59e0b', badge: 'NEW' },
            ]
        },
        {
            // 2026-09-08: SEO & Visibility collapsed into two standalone entries (no section header).
            category: 'seo',
            categoryLabel: '',
            standalone: true,
            items: [
                { href: 'https://audit.optilinkai.com', label: 'Instant Site Audit', icon: '⚡', color: '#059669', external: true, badge: 'LIVE', pulse: true },
                { href: '/link-building', label: 'AI Auto Linkbuilding', icon: '🤖', color: '#2563eb', badge: 'AUTO', pulse: true },
                { href: '/indexer', label: 'Instant Indexing & Pings', icon: '⚡', color: '#7c3aed', badge: 'NEW', pulse: true },
                { href: 'https://voxychat.co', label: 'Build a Chatbot', icon: '💬', color: '#2563eb', external: true },
            ]
        },
        {
            category: 'agency',
            categoryLabel: 'Agency & Clients',
            items: [
                { href: '/agency-hq', label: 'Agency HQ', icon: '🏢', color: '#2563eb', badge: 'NEW', pulse: true },
                { href: '/proposals', label: 'Proposals', icon: '📝', color: '#2563eb', badge: 'NEW', pulse: true },
                { href: '/invoices', label: 'Invoices', icon: '🧾', color: '#10b981', badge: 'NEW', pulse: true },
                { href: '/clients', label: 'Clients', icon: '🏢', color: '#2563eb', badge: 'AGENCY' },
                { href: '/portal', label: 'Client Portal', icon: '🪟', color: '#2563eb', badge: 'NEW', pulse: true },
                { href: '/reports', label: 'Monthly Reports', icon: '📄', color: '#10b981', badge: 'NEW' },
                { href: '/review-responder', label: 'Reviews', icon: '⭐', color: '#f59e0b', badge: 'NEW', pulse: true },
            ]
        },
        {
            category: 'system',
            categoryLabel: 'System',
            items: [
                { href: '/accounts', label: 'Sender Accounts', icon: '🔑', color: '#64748b' },
                { href: '/connect-ai', label: 'Connect AI Assistant', icon: '🔌', color: '#2563eb' },   // moved from the hidden AI Autopilot section
                { href: '/cloud-sending', label: '24/7 Cloud Sending', icon: '🌐', color: '#2563eb', badge: 'NEW', pulse: true },
                { href: '/cloud-ai', label: '24/7 AI SDR & Automations', icon: '🤖', color: '#2563eb', badge: 'NEW', pulse: true },
                { href: '/local-ai', label: 'Local AI (private)', icon: '🖥️', color: '#2563eb', badge: 'NEW', pulse: true },
                { href: '/crm', label: 'CRM Sync', icon: '🧲', color: '#2563eb', badge: 'NEW', pulse: true },
                { href: '/deliverability', label: 'Deliverability', icon: '🛡️', color: '#10b981', badge: 'NEW', pulse: true },
                { href: '/system-health', label: 'System Health', icon: '💚', color: '#10b981', badge: 'NEW' },
                { href: '/models', label: 'AI Models & Connectors', icon: '🧠', color: '#2563eb', badge: 'NEW', pulse: true },
                { href: '/settings', label: 'Settings', icon: '⚙️', color: '#64748b' },
                { href: '/upgrade', label: 'License & Plan', icon: '👑', color: '#f59e0b' }
            ]
        },
        {
            category: 'getmore',
            categoryLabel: 'Get More',
            items: [
                { href: '/linkbuilder', label: 'Download Linkbuilder', icon: '🔗', color: '#2563eb', badge: 'FREE', pulse: true },
                { href: '#find-leads', action: 'findLeads', label: 'Get Leads (Browser)', icon: '🎯', color: '#ea580c' },
                { href: 'https://gptleadshunter.com',   label: 'Visit Home Page', icon: '🏠', color: '#ea580c', external: true, ownerOnly: true },
                { href: 'https://gptleadshunter.com/#optilink', label: 'Get Backlinks', icon: '🔗', color: '#2563eb', external: true, badge: 'HOT', ownerOnly: true },
                { href: 'https://audit.optilinkai.com', label: 'SEO AUDIT',     icon: '🛡️', color: '#059669', external: true, badge: 'PRO', ownerOnly: true }
            ]
        }
    ];

    // Hidden routes that are not standalone sidebar entries but are still
    // surfaced via the in-page ConsolidatedNav tab bar AND via search.
    // Listing them here means typing "audit" or "site scan" still finds them.
    const HIDDEN_SEARCHABLE = [
        // Rank Tracker is now a visible item in the SEO group (above).
        // 2026-09-10: Instant Indexing is a VISIBLE item again (/indexer). It was
        // hidden when it lived inside the Auto-SEO Engine; that engine is gone, so
        // the indexer is standalone once more and needs its own menu entry.
    ];

    // Whitelabel: when hideOriginalBranding is on, drop the entire
    // "Get More" section so clients don't see external GLH links.
    const effectiveMenuItems = branding.hideOriginalBranding
        ? menuItems.filter(s => s.category !== 'getmore')
        : menuItems;

    // Filter items based on search query — and also include hidden searchable
    // routes when there's a query active. They are only shown while searching.
    // Hidden product areas (lib/hidden_features.js): whole sections and any stray
    // link into them disappear from the menu and from search.
    const visibleMenuItems = effectiveMenuItems
        .filter(section => !HIDDEN_SECTIONS.includes(section.category))
        .map(section => ({ ...section, items: section.items.filter(item => !isHiddenHref(item.href)) }))
        .filter(section => section.items.length > 0);
    const filteredMenuItems = visibleMenuItems.map(section => {
        const matchingItems = section.items.filter(item =>
            (!item.ownerOnly || ownerMode) &&
            item.label.toLowerCase().includes(searchQuery.toLowerCase())
        );
        return {
            ...section,
            items: matchingItems
        };
    }).filter(section => section.items.length > 0);

    if (searchQuery.trim().length > 0) {
        const hiddenMatches = HIDDEN_SEARCHABLE.filter(item =>
            item.label.toLowerCase().includes(searchQuery.toLowerCase())
        );
        if (hiddenMatches.length > 0) {
            filteredMenuItems.push({
                category: 'hidden',
                categoryLabel: 'Inside Consolidated Tabs',
                items: hiddenMatches,
            });
        }
    }


    // Editorial nav: no icons, no colour chips. The active route is a short
    // ink underline; everything else is quiet text that darkens on hover.
    const monogram = (label) => {
        const words = String(label || '').replace(/[^\p{L}\p{N} ]/gu, '').trim().split(/\s+/).filter(Boolean);
        if (words.length === 0) return '·';
        if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
        return (words[0][0] + words[1][0]).toUpperCase();
    };

    const linkStyle = (href) => {
        const active = isActive(href);
        return {
            display: 'flex',
            alignItems: 'center',
            justifyContent: effectiveCollapsed ? 'center' : 'flex-start',
            gap: effectiveCollapsed ? 0 : '10px',
            padding: effectiveCollapsed ? '8px 0' : '9px 12px',
            borderRadius: '8px',
            color: 'var(--text-primary)',
            textDecoration: 'none',
            fontSize: '15.5px',
            fontWeight: active ? 800 : 600,
            letterSpacing: '-0.005em',
            transition: 'color 0.18s ease, background-color 0.18s ease, transform 0.18s ease',
            background: active ? 'var(--bg-card)' : 'transparent',
            boxShadow: active ? 'inset 0 0 0 1px var(--hairline-2)' : 'none',
            border: 'none',
            position: 'relative',
            marginBottom: '1px',
            lineHeight: 1.35
        };
    };

    const Mono = ({ label, active }) => (
        <span aria-hidden="true" style={{
            width: '34px', height: '34px', borderRadius: '8px', flexShrink: 0,
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            fontSize: '11px', fontWeight: 800, letterSpacing: '0.04em',
            background: active ? 'var(--ink)' : 'var(--bg-card)',
            color: active ? 'var(--paper)' : 'var(--ink-2)',
            border: active ? '1px solid var(--ink)' : '1px solid var(--hairline)'
        }}>{monogram(label)}</span>
    );

    const Label = ({ item, active }) => (
        <span style={{ flexGrow: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span style={{
                lineHeight: 1.25,
                borderBottom: active ? '2px solid var(--blue)' : '2px solid transparent',
                paddingBottom: '1px'
            }}>{item.label}</span>
            {item.external && <span aria-hidden="true" style={{ fontSize: '12px', color: 'var(--ink-2)' }}>↗</span>}
            {item.badge === 'LIVE' && <span aria-hidden="true" style={{ width: '6px', height: '6px', borderRadius: '50%', background: 'var(--color-success)', flexShrink: 0 }} />}
        </span>
    );

    const Tag = ({ text }) => (
        <span style={{
            fontSize: '10.5px', fontWeight: 800, letterSpacing: '0.08em',
            color: 'var(--text-primary)', border: '1px solid var(--hairline-2)',
            padding: '1px 6px', borderRadius: '999px', flexShrink: 0
        }}>{text}</span>
    );

    const ghostBtn = {
        display: 'flex', alignItems: 'center', gap: '8px', width: '100%',
        padding: '8px 10px', borderRadius: '999px',
        border: '1px solid var(--hairline-2)', background: 'transparent',
        color: 'var(--text-primary)', fontSize: '14px', fontWeight: 700, cursor: 'pointer', textAlign: 'left',
        fontFamily: 'var(--font-body)'
    };

    const SHOW_BADGE = new Set(['OWNER', 'FREE', 'PRO']);

    return (
        <aside style={{
            width: effectiveCollapsed ? '84px' : '288px',
            background: 'var(--sidebar-bg)',
            borderRight: '1px solid var(--hairline)',
            padding: effectiveCollapsed ? '22px 12px' : '22px 18px',
            display: 'flex',
            flexDirection: 'column',
            gap: '14px',
            position: 'fixed',
            height: '100vh',
            top: 0,
            left: 0,
            overflowY: 'auto',
            overflowX: 'hidden',
            zIndex: 100,
            transition: 'width 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            scrollbarWidth: 'none'
        }}>
            {/* BRAND */}
            <div style={{ display: 'flex', flexDirection: effectiveCollapsed ? 'column' : 'row', alignItems: 'center', justifyContent: effectiveCollapsed ? 'center' : 'space-between', gap: '10px', minHeight: '40px' }}>
                <Link href="/dashboard" style={{ display: 'flex', alignItems: 'center', gap: '10px', textDecoration: 'none', minWidth: 0 }}>
                    {branding.logoUrl ? (
                        <span style={{ width: '32px', height: '32px', borderRadius: '8px', background: `url(${branding.logoUrl}) center/cover`, flexShrink: 0, border: '1px solid var(--hairline)' }} />
                    ) : effectiveCollapsed ? (
                        <span style={{ fontFamily: 'var(--font-display)', fontSize: '26px', lineHeight: 1, color: 'var(--text-primary)' }}>O<span style={{ color: 'var(--blue)' }}>.</span></span>
                    ) : null}
                    {!effectiveCollapsed && (
                        <span style={{ minWidth: 0 }}>
                            <h1 style={{ fontFamily: 'var(--font-display)', fontWeight: 400, fontSize: '25px', letterSpacing: '-0.01em', lineHeight: 1.05, color: 'var(--text-primary)', margin: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {branding.hideOriginalBranding || (branding.agencyName && branding.agencyName !== 'Optilink AI Command Center')
                                    ? branding.agencyName
                                    : <>Optilink<span style={{ color: 'var(--blue)' }}>.</span></>}
                            </h1>
                            <p style={{ fontSize: '11.5px', color: 'var(--ink-2)', letterSpacing: '0.1em', textTransform: 'uppercase', margin: '3px 0 0', fontWeight: 700 }}>
                                {branding.tagline || 'AI Command Center'}
                            </p>
                        </span>
                    )}
                </Link>
                {!isMobile && (
                    <button
                        onClick={toggleCollapse}
                        aria-label={effectiveCollapsed ? 'Expand menu' : 'Collapse menu'}
                        style={{
                            width: '26px', height: '26px', borderRadius: '50%', flexShrink: 0,
                            background: 'transparent', border: '1px solid var(--hairline-2)', color: 'var(--ink-2)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', fontSize: '11px'
                        }}
                    >{effectiveCollapsed ? '›' : '‹'}</button>
                )}
            </div>

            {/* WORKSPACE (CLIENT) SWITCHER — agency multi-tenancy */}
            <div style={{ marginTop: '4px', display: effectiveCollapsed ? 'flex' : 'block', justifyContent: 'center' }}>
                <WorkspaceSwitcher collapsed={effectiveCollapsed} />
            </div>

            {/* SEARCH */}
            {!effectiveCollapsed && (
                <input
                    suppressHydrationWarning
                    type="text"
                    placeholder="Search…"
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                    style={{ width: '100%', borderRadius: '999px', padding: '8px 16px', fontSize: '14px', minHeight: '40px' }}
                />
            )}

            {/* NAVIGATION */}
            <nav style={{ display: 'flex', flexDirection: 'column', gap: simpleMode && !searchQuery.trim() ? '2px' : '20px', marginTop: '2px', flexGrow: 1 }}>
                {simpleMode && !searchQuery.trim() && SIMPLE_ITEMS.map((item) => {
                    const active = !item.external && isActive(item.href);
                    const NavTag = item.external ? 'a' : Link;
                    const extra = item.external ? { target: '_blank', rel: 'noopener noreferrer' } : {};
                    return (
                        <NavTag key={item.href} href={item.href} {...extra} style={{ ...linkStyle(item.external ? '__never_active__' : item.href), fontSize: '16px', padding: effectiveCollapsed ? '6px 0' : '10px 12px' }} title={effectiveCollapsed ? item.label : (item.external ? `Opens ${item.href} in a new tab` : '')}>
                            {effectiveCollapsed ? <Mono label={item.label} active={active} /> : <Label item={item} active={active} />}
                        </NavTag>
                    );
                })}
                {simpleMode && !searchQuery.trim() && !effectiveCollapsed && (
                    <button onClick={toggleSimple} style={{ ...ghostBtn, marginTop: '14px' }}>
                        All features <span style={{ marginLeft: 'auto', color: 'var(--ink-3)' }}>→</span>
                    </button>
                )}

                {(!simpleMode || searchQuery.trim()) && filteredMenuItems.map((section) => {
                    const isExpanded = expandedSections[section.category];
                    return (
                        <div key={section.category} style={{ display: 'flex', flexDirection: 'column' }}>
                            {!effectiveCollapsed && !section.standalone && (
                                <div
                                    onClick={() => toggleSection(section.category)}
                                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 12px', marginBottom: '4px', cursor: 'pointer', userSelect: 'none', borderRadius: '8px', background: 'var(--bg-subtle)' }}
                                >
                                    <p style={{ margin: 0, fontFamily: 'var(--font-body)', fontWeight: 800, fontSize: '14px', color: 'var(--text-primary)', letterSpacing: '0.08em', textTransform: 'uppercase', lineHeight: 1.2 }}>{section.categoryLabel}</p>
                                    <span style={{ fontSize: '16px', fontWeight: 700, color: 'var(--text-primary)', transition: 'transform 0.2s', transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)' }}>›</span>
                                </div>
                            )}
                            {effectiveCollapsed && !section.standalone && (
                                <div aria-hidden="true" style={{ height: '1px', background: 'var(--hairline)', margin: '2px 10px 8px' }} />
                            )}

                            {(effectiveCollapsed || isExpanded || section.standalone || searchQuery.trim().length > 0) && (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '0px', animation: 'fadeIn 0.2s forwards' }}>
                                    {section.items.filter((item) => !item.ownerOnly || ownerMode).map((item) => {
                                        const active = !item.external && isActive(item.href);
                                        const externalProps = item.external ? { target: '_blank', rel: 'noopener noreferrer' } : {};
                                        const NavTag = item.external ? 'a' : Link;
                                        return (
                                            <NavTag
                                                key={item.href}
                                                href={item.href}
                                                {...externalProps}
                                                onClick={item.action === 'findLeads' ? (e) => { e.preventDefault(); launchLeadScraper(); } : undefined}
                                                style={linkStyle(item.external ? '__never_active__' : item.href)}
                                                title={effectiveCollapsed ? item.label : (item.external ? `Opens ${item.href} in a new tab` : '')}
                                            >
                                                {effectiveCollapsed ? <Mono label={item.label} active={active} /> : (
                                                    <>
                                                        <Label item={item} active={active} />
                                                        {item.badge && SHOW_BADGE.has(item.badge) && <Tag text={item.badge} />}
                                                    </>
                                                )}
                                            </NavTag>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    );
                })}
                {(!simpleMode || searchQuery.trim()) && !effectiveCollapsed && (
                    <button onClick={toggleSimple} style={{ ...ghostBtn, marginTop: '4px' }}>
                        ← Back to simple menu
                    </button>
                )}
            </nav>

            {/* FOOTER */}
            <div style={{ marginTop: 'auto', paddingTop: '12px', borderTop: '1px solid var(--hairline)' }}>
                {effectiveCollapsed ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', alignItems: 'center' }}>
                        <Link href="/accounts" title="Sender Accounts" style={{ ...linkStyle('/accounts'), padding: '4px 0' }}><Mono label="Sender Accounts" active={isActive('/accounts')} /></Link>
                        <Link href="/settings" title="Settings" style={{ ...linkStyle('/settings'), padding: '4px 0' }}><Mono label="Settings" active={isActive('/settings')} /></Link>
                    </div>
                ) : (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', padding: '0 10px' }}>
                        <span style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
                            <span aria-hidden="true" style={{ width: '7px', height: '7px', borderRadius: '50%', background: 'var(--color-success)', flexShrink: 0 }} />
                            <span style={{ fontSize: '13px', color: 'var(--text-primary)', fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>Outbound active</span>
                        </span>
                        <span style={{ display: 'flex', gap: '10px', flexShrink: 0 }}>
                            <Link href="/accounts" style={{ fontSize: '13px', color: 'var(--text-primary)', fontWeight: 700, borderBottom: '1px solid var(--hairline-2)' }}>Accounts</Link>
                            <Link href="/settings" style={{ fontSize: '13px', color: 'var(--text-primary)', fontWeight: 700, borderBottom: '1px solid var(--hairline-2)' }}>Settings</Link>
                        </span>
                    </div>
                )}
            </div>
        </aside>
    );
}
